import React from 'react';
import styled from 'styled-components';
import {
  FaChartLine,
  FaClipboardCheck,
  FaMoneyBillWave,
  FaFileInvoiceDollar,
  FaHandHoldingUsd,
  FaSpinner,
  FaEye,
  FaFly,
  FaCalendar,
  FaReceipt,
  FaExclamationTriangle,
  FaCreditCard,
  FaBuilding
} from 'react-icons/fa';
import { formatCurrency } from '../../utils/utils';

const OverviewContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const OverviewTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  margin: 0 0 ${props => props.theme.spacing(3)} 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
`;

const SectionLabel = styled.h3`
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin: ${props => props.theme.spacing(3)} 0 ${props => props.theme.spacing(2)} 0;
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${props => props.theme.spacing(2)};

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)};
  display: flex;
  flex-direction: column;
  border-top: 3px solid ${props => props.accent || props.theme.colors.primary};
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const MetricIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${props => props.color || props.theme.colors.primary}22;
  color: ${props => props.color || props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing(1)};

  svg {
    font-size: 1.2rem;
  }
`;

const MetricValue = styled.div`
  font-size: ${props => props.theme.typography.fontSize.extraLarge};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.color || props.theme.colors.textPrimary};
  margin: ${props => props.theme.spacing(1)} 0;
`;

const MetricLabel = styled.div`
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 150px;

  svg {
    font-size: 2rem;
    color: ${props => props.theme.colors.primary};
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 150px;
  color: ${props => props.theme.colors.error};
  text-align: center;
  padding: ${props => props.theme.spacing(2)};
`;

const FinancialOverview = ({ data, loading, error }) => {
  if (loading) {
    return <LoadingContainer><FaSpinner /></LoadingContainer>;
  }

  if (error) {
    return <ErrorContainer>{error}</ErrorContainer>;
  }

  if (!data) {
    return <ErrorContainer>No financial data available</ErrorContainer>;
  }

  return (
    <OverviewContainer>
      <OverviewTitle>
        <FaChartLine />
        Financial Overview
      </OverviewTitle>

      {/* REVENUE & PROFIT */}
      <SectionLabel>Revenue & Profit</SectionLabel>
      <OverviewGrid>
        <MetricCard accent="#28a745">
          <MetricIcon color="#28a745"><FaMoneyBillWave /></MetricIcon>
          <MetricLabel>Total Revenue</MetricLabel>
          <MetricValue color="#28a745">{formatCurrency(data.totalRevenue)}</MetricValue>
        </MetricCard>

        <MetricCard accent="#007bff">
          <MetricIcon color="#007bff"><FaCreditCard /></MetricIcon>
          <MetricLabel>Payments Received</MetricLabel>
          <MetricValue color="#007bff">{formatCurrency(data.totalPaymentsReceived)}</MetricValue>
        </MetricCard>

        <MetricCard accent="#dc3545">
          <MetricIcon color="#dc3545"><FaHandHoldingUsd /></MetricIcon>
          <MetricLabel>Total Costs</MetricLabel>
          <MetricValue color="#dc3545">{formatCurrency(data.totalAllCosts || data.totalCosts)}</MetricValue>
        </MetricCard>

        <MetricCard accent={parseFloat(data.trueProfit || data.totalProfit) >= 0 ? '#28a745' : '#dc3545'}>
          <MetricIcon color={parseFloat(data.trueProfit || data.totalProfit) >= 0 ? '#28a745' : '#dc3545'}><FaChartLine /></MetricIcon>
          <MetricLabel>Net Profit</MetricLabel>
          <MetricValue color={parseFloat(data.trueProfit || data.totalProfit) >= 0 ? '#28a745' : '#dc3545'}>
            {formatCurrency(data.trueProfit || data.totalProfit)}
          </MetricValue>
        </MetricCard>
      </OverviewGrid>

      {/* INVOICING */}
      <SectionLabel>Invoicing</SectionLabel>
      <OverviewGrid>
        <MetricCard accent="#F69620">
          <MetricIcon color="#F69620"><FaExclamationTriangle /></MetricIcon>
          <MetricLabel>Outstanding Invoices</MetricLabel>
          <MetricValue color="#F69620">{formatCurrency(data.totalUnpaidInvoices)}</MetricValue>
        </MetricCard>

        <MetricCard accent="#3f51b5">
          <MetricIcon color="#3f51b5"><FaFileInvoiceDollar /></MetricIcon>
          <MetricLabel>Total Invoiced</MetricLabel>
          <MetricValue>{formatCurrency(data.totalInvoiced)}</MetricValue>
        </MetricCard>

        <MetricCard accent="#9c27b0">
          <MetricIcon color="#9c27b0"><FaReceipt /></MetricIcon>
          <MetricLabel>Business Expenses</MetricLabel>
          <MetricValue color="#9c27b0">{formatCurrency(data.totalBusinessExpenses)}</MetricValue>
        </MetricCard>

        <MetricCard accent="#00bcd4">
          <MetricIcon color="#00bcd4"><FaBuilding /></MetricIcon>
          <MetricLabel>Project Costs</MetricLabel>
          <MetricValue>{formatCurrency(data.totalCosts)}</MetricValue>
        </MetricCard>
      </OverviewGrid>

      {/* QUOTES PIPELINE */}
      <SectionLabel>Quotes Pipeline</SectionLabel>
      <OverviewGrid>
        <MetricCard>
          <MetricIcon><FaFileInvoiceDollar /></MetricIcon>
          <MetricLabel>Total Quotes</MetricLabel>
          <MetricValue>{data.totalEstimates}</MetricValue>
        </MetricCard>

        <MetricCard accent="#007bff">
          <MetricIcon color="#007bff"><FaFly /></MetricIcon>
          <MetricLabel>Sent</MetricLabel>
          <MetricValue>{data.totalSent}</MetricValue>
          <MetricLabel>{formatCurrency(data.totalSentAmount)}</MetricLabel>
        </MetricCard>

        <MetricCard accent="#28a745">
          <MetricIcon color="#28a745"><FaClipboardCheck /></MetricIcon>
          <MetricLabel>Accepted</MetricLabel>
          <MetricValue>{data.totalAccepted}</MetricValue>
          <MetricLabel>{formatCurrency(data.totalAcceptedAmount)}</MetricLabel>
        </MetricCard>

        <MetricCard accent="#dc3545">
          <MetricIcon color="#dc3545"><FaHandHoldingUsd /></MetricIcon>
          <MetricLabel>Declined</MetricLabel>
          <MetricValue>{data.totalDeclined}</MetricValue>
          <MetricLabel>{formatCurrency(data.totalDeclinedAmount)}</MetricLabel>
        </MetricCard>

        <MetricCard accent="#F69620">
          <MetricIcon color="#F69620"><FaEye /></MetricIcon>
          <MetricLabel>Viewed</MetricLabel>
          <MetricValue>{data.totalViewed}</MetricValue>
          <MetricLabel>{formatCurrency(data.totalViewedAmount)}</MetricLabel>
        </MetricCard>
      </OverviewGrid>

      {/* JOBS */}
      <SectionLabel>Jobs</SectionLabel>
      <OverviewGrid>
        <MetricCard>
          <MetricIcon><FaCalendar /></MetricIcon>
          <MetricLabel>Scheduled Jobs</MetricLabel>
          <MetricValue>{(data.rangeJobs || 0) + (data.singleDayJobs || 0)}</MetricValue>
          <MetricLabel>{formatCurrency(data.totalScheduledAmount)}</MetricLabel>
        </MetricCard>
      </OverviewGrid>
    </OverviewContainer>
  );
};

export default FinancialOverview;
